import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import useDetailPageTabHeight from "@app-contexts/public/features/event-detail/hooks/useDetailPageTabHeight";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { formatRelativeStaticPath } from "@shared/utils/images/utils";

import usePublicEventService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import { PublicBaseEventSerializer } from "@app-contexts/public/features/event-detail/data/types/events";
import HeaderDetails from "@app/core/src/shared/components/application/data-display/HeaderDetails/HeaderDetails.web";
import PageMaxWidth from "@app/core/src/shared/components/ui-kit/layout/PageMaxWidth/PageMaxWidth.web";
import EventBadges from "@app/core/src/shared/modules/events/components/event-details/EventBadges/EventBadges.web";
import { getDisplayableRating } from "@app/core/src/shared/modules/ratings/utils/ratings";
import useDjangoWaffle from "@app/core/src/shared/hooks/djangoWaffle/useDjangoWaffle";
import Grid from "@ui-kit/layout/Grid/Grid.web";
import GridItem from "@ui-kit/layout/GridItem/GridItem.web";
import SimpleGrid from "@ui-kit/layout/Grid/SimpleGrid.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import CardImage from "@ui-application/media/CardImage/CardImage.web";
import EventStatuses from "@ui-kit/data-display/EventStatuses/EventStatuses.web";
import moment from "moment-timezone";
import { useTranslation } from "@shared/utils/internationalization";
import { useRouter } from "next/router";
import { Dispatch, JSX, SetStateAction } from "react";
import { breakpoints, domElementIds } from "@/styles/constants";
import useShowHeaderActionInfo from "@/hooks/useShowHeaderActionInfo";
import CallToAction from "@/components/molecules/headers/details-page/CallToAction";
import HeaderAction from "./HeaderAction";
import HeaderTabs from "./HeaderTabs";

const { sm, lg } = breakpoints;
const { headerTabsId } = domElementIds;

export default function EventDetailsHeader({
  eventTabOptions,
  selectedTab,
  setSelectedTab,
  openContactDetailsModal,
  isSmallerThanSm,
  isTablet,
  isReactNativeApp,
  handleViewMap,
  openModal,
  isAttractionPage,
  reviewsOnClick,
  scrollSyncDisabled,
}: {
  isTablet: boolean;
  selectedTab: number;
  openModal: () => void;
  isSmallerThanSm: boolean;
  isAttractionPage: boolean;
  isReactNativeApp: boolean;
  openContactDetailsModal: () => void;
  handleViewMap: (input: boolean) => void;
  setSelectedTab: Dispatch<SetStateAction<number>>;
  eventTabOptions: Array<{
    id: number;
    title?: string;
    showTab: boolean;
    hash?: string;
  }>;
  reviewsOnClick?: () => void;
  /** When true, scroll-based tab sync is disabled (e.g., during URL navigation) */
  scrollSyncDisabled?: boolean;
}) {
  // Hooks
  const router = useRouter();
  const { hasItems } = usePublicBasket();
  const { t } = useTranslation("purchases");
  const tabHeight = useDetailPageTabHeight(hasItems);
  const showHeaderActionInfo = useShowHeaderActionInfo();

  const eventParams = {
    purchase_platform: enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFFIRM.id,
    ].toString(),
    source_channel: router?.query?.source_channel?.toString(),
  };

  const { useRetrieveWithMembershipTicketTypesOverride: useGetWithSlug } =
    usePublicEventService();
  const { data: eventData, isLoading } = useGetWithSlug(
    String(router?.query?.eventSlug),
    {
      config: {
        params: eventParams,
      },
      options: {
        enabled: Boolean(router?.query?.eventSlug?.toString()),
      },
    }
  );

  const { isFlagActive } = useDjangoWaffle();
  const venueId = eventData?.venue?.id;
  const isRatingEnabled = isFlagActive("enable_rating_display", venueId);
  const displayRating = isRatingEnabled
    ? getDisplayableRating(eventData?.rating, eventData?.venue?.rating)
    : null;

  return (
    <>
      <Flex direction="column" backgroundColor="bg">
        <PageMaxWidth
          // sm padding needs to match the padding in the PageMaxWidth component
          padding={{ base: "none", sm: "0 40px" }}
        >
          <HeaderDetails
            isTablet={isTablet}
            isLoading={isLoading}
            title={eventData?.name}
            saleStartsOn={eventData?.event_sale_starts_on}
            timezone={eventData?.timezone}
            organizer={eventData?.venue}
            subtitle={eventData?.subtitle}
            isSmallerThanSm={isSmallerThanSm}
            openContactDetailsModal={openContactDetailsModal}
            imageElement={<EventDetailsHeader.Image event={eventData} />}
            statusElement={
              <EventDetailsHeader.Status
                statuses={EventStatuses({ event_data: eventData })}
              />
            }
            badgeElement={
              <EventDetailsHeader.Badge
                badges={EventBadges({
                  isSmallerThanSm,
                  eventData,
                  eventTabOptions,
                  selectedTab,
                  setSelectedTab,
                  handleViewMap,
                  isAttractionPage,
                })}
              />
            }
            rating={displayRating}
            onClickRating={reviewsOnClick}
          />
        </PageMaxWidth>
      </Flex>

      <Flex
        top={tabHeight}
        position="sticky"
        backgroundColor="bg"
        zIndex="navbarTabs"
        borderBottom={!isReactNativeApp ? "1px solid" : "0px solid"}
        borderBottomColor="gray.400"
      >
        <Box
          w="full"
          mx="auto"
          flexGrow={1}
          maxWidth="container.content"
          position="sticky"
          hidden={selectedTab === undefined}
        >
          {!isAttractionPage && (
            <CallToAction
              eventData={eventData}
              venue={eventData.venue}
              openModal={openModal}
              isSmallerThanSm={isSmallerThanSm}
            />
          )}

          <Grid h="full" width="full" templateColumns="repeat(5, 1fr)">
            <GridItem id={headerTabsId} colSpan={{ base: 5, lg: 3 }}>
              <HeaderTabs
                tabOptionsList={eventTabOptions}
                selectedTab={selectedTab}
                setSelectedTab={setSelectedTab}
                scrollSyncDisabled={scrollSyncDisabled}
              />
            </GridItem>
            <GridItem
              h="0"
              pl="xs"
              pr="3xl"
              colSpan={2}
              display={{ base: "none", lg: "block" }}
            >
              <HeaderAction
                eventData={eventData}
                onModalOpen={openModal}
                venue={eventData?.venue}
                titleText={eventData?.name}
                showText={showHeaderActionInfo}
                isAttractionPage={Boolean(eventData.attraction_event_config)}
                locationText={
                  eventData?.location && !eventData?.virtual_event_type
                    ? `${eventData.location.name} ${eventData.location.street_name}, ${eventData.location.city}`
                    : undefined
                }
                startsOnText={
                  eventData?.date_time_to_be_determined
                    ? t("Date and Time TBD")
                    : `${moment(eventData?.starts_on)
                        .tz(eventData?.timezone)
                        .format("ddd MMM D, YYYY | h:mm A z")}`
                }
              />
            </GridItem>
          </Grid>
        </Box>
      </Flex>
    </>
  );
}

EventDetailsHeader.Badge = function EventDetailsHeaderBadge({
  badges,
}: {
  badges: Array<{
    id: number;
    icon: JSX.Element;
    text: JSX.Element;
    showBadge: boolean;
    dataTestId: string;
  }>;
}) {
  const hasBadges = badges.some((badge) => badge.showBadge);

  return (
    <>
      <SimpleGrid
        mt="xs"
        columns={{ base: 1, sm: 2 }}
        fontSize={{ base: "xs", sm: "sm" }}
        display={hasBadges ? "grid" : "none"}
      >
        {badges.map(
          ({ id, showBadge, dataTestId, icon, text }) =>
            showBadge && (
              <Flex
                key={id}
                mr={{ base: "none", sm: "lg" }}
                py="xs"
                alignItems={{ base: "center", sm: "flex-start" }}
                data-testid={dataTestId}
              >
                {icon} {text}
              </Flex>
            )
        )}
      </SimpleGrid>
    </>
  );
};

EventDetailsHeader.Status = function EventDetailsHeaderStatus({
  statuses,
}: {
  statuses: Array<{
    id: number;
    element: JSX.Element;
    showElement: boolean;
  }>;
}) {
  return (
    <Flex
      mt="xs"
      alignItems="center"
      hidden={!statuses.some(({ showElement }) => showElement)}
    >
      {statuses.map(
        ({ id, element, showElement }) =>
          showElement && (
            <Flex key={id} mr="md">
              {element}
            </Flex>
          )
      )}
    </Flex>
  );
};

EventDetailsHeader.Image = function EventDetailsHeaderImage({
  event,
}: {
  event: PublicBaseEventSerializer;
}) {
  const { t } = useTranslation("purchases");
  let src = formatRelativeStaticPath("images/default-banner.png");
  if (event.image_banner) {
    src = event.image_banner;
  }

  return (
    <Box
      overflow="hidden"
      borderRadius={{ sm: "md" }}
      aspectRatio="2 / 1"
      w={{ base: "100%", sm: "auto", lg: "100%" }}
      // eslint-disable-next-line showpass-custom/no-hardcoded-style-values
      h={{ base: "100%", sm: "117px", lg: "100%" }}
    >
      <CardImage
        src={src}
        width={466}
        height={233}
        alt={t("Event image for {{eventName}}", {
          eventName: event?.name,
        })}
        title={t("Event image for {{eventName}}", {
          eventName: event?.name,
        })}
        ariaLabel={t("Event image for {{eventName}}", {
          eventName: event?.name,
        })}
        favoriteData={{
          event,
          displayRight: true,
        }}
        sizes={`
          (max-width: ${sm}px) 100vw,
          ((min-width: ${sm}px) and (max-width: ${lg}px)) ${256}px,
          (min-width: ${lg}px) ${864}px,
        `}
        useServerlessResize
        aspectRatio="2"
      />
    </Box>
  );
};
